import { OASElement } from '@oas-ui/core'
import { isRtl } from '../../shared/direction.js'
import { beginScrub, scrubValueAt, roundToPrecision, type ScrubAnchor, SCRUB_PX_PER_STEP } from '../../shared/scrub.js'

/** 单个刻度：数值 + 展示标签（show-stops 生成的刻度点无标签） */
interface MarkEntry {
  value: number
  label: string
}

/** 数值输入框防抖提交时长（ms） */
const DEBOUNCE_MS = 300

/** 默认 thumb 直径（px），用于把百分比换算成像素定位（尺寸档位见 THUMB_SIZES） */
const THUMB_SIZE = 14

/** show-stops 刻度点数量上限（超过不渲染，防 DOM 爆炸） */
const MAX_STOPS = 100

/** step="mark" 模式键盘大步跳过的刻度档数 */
const MARK_BIG_STEP = 3

/** 尺寸档 thumb 直径表（与 CSS --oas-slider-thumb-size 各档取值成对维护） */
const THUMB_SIZES: Record<string, number> = { sm: 10, md: 14, lg: 18 }

/** 预设语义色名 → token 变量（非预设值原样透传；不硬编码色值，暗色变体随 token 切换） */
const COLOR_TOKENS: Record<string, string> = {
  primary: 'var(--oas-color-primary)',
  success: 'var(--oas-color-success)',
  warning: 'var(--oas-color-warning)',
  danger: 'var(--oas-color-danger)',
}

/** tooltip-position 合法值 */
const TOOLTIP_POSITIONS = new Set(['top', 'bottom', 'left', 'right'])

function clampNum(v: number, min: number, max: number): number {
  return Math.min(Math.max(v, min), max)
}

/** 解析多把手 value：JSON 数组 `[a, b, c]` 或逗号分隔 `"a,b,c"`（长度 ≥2），非法返回 null */
function parseValueArray(raw: string): number[] | null {
  if (!raw.trim()) return null
  let parsed: unknown
  try {
    parsed = JSON.parse(raw)
  } catch {
    parsed = raw.split(',').map((s) => Number(s.trim()))
  }
  if (!Array.isArray(parsed) || parsed.length < 2) return null
  if (!parsed.every((n) => typeof n === 'number' && Number.isFinite(n))) return null
  return parsed as number[]
}

const STYLE = `
:host {
  display: inline-block;
  font-family: inherit;
  width: 100%;
  min-width: 120px;
  /* 尺寸档变量（与 THUMB_SIZES 常量表成对维护） */
  --oas-slider-track-size: 4px;
  --oas-slider-thumb-size: 14px;
  /* 颜色通道：color / track-color 属性映射到宿主 CSS 变量，任意值可覆盖 */
  --oas-slider-color: var(--oas-color-primary);
  --oas-slider-track: var(--oas-color-border);
}
:host([hidden]) {
  display: none;
}
:host([data-size='sm']) {
  --oas-slider-track-size: 3px;
  --oas-slider-thumb-size: 10px;
}
:host([data-size='lg']) {
  --oas-slider-track-size: 6px;
  --oas-slider-thumb-size: 18px;
}
/* 垂直模式：宿主宽度收缩为轨道宽度，高度走变量（默认 200px） */
:host([data-vertical]) {
  width: auto;
  min-width: 0;
}
.wrap {
  display: flex;
  align-items: center;
  gap: var(--oas-space-3);
}
:host([data-vertical]) .wrap {
  flex-direction: column;
  align-items: center;
}
.slider-col {
  flex: 1 1 auto;
  min-width: 0;
}
:host([data-vertical]) .slider-col {
  display: flex;
  align-items: stretch;
  flex: 0 0 auto;
}
.track-wrap {
  position: relative;
  height: 20px;
}
:host([data-vertical]) .track-wrap {
  width: 20px;
  height: var(--oas-slider-height, 200px);
}
/* 灰色轨道底层：由 track-wrap 伪元素承担。原生 input 的 track 背景必须透明——
   range 模式 pointerdown 提升 input z-index 抢拖动权时，若灰轨道画在原生 track 上
   会随之上浮盖住 .fill（蓝色区间填充消失） */
.track-wrap::before {
  content: '';
  position: absolute;
  top: calc((20px - var(--oas-slider-track-size)) / 2);
  left: 0;
  right: 0;
  height: var(--oas-slider-track-size);
  border-radius: var(--oas-slider-track-size);
  /* 轨道底色走 token；色轨（track 预设）由 JS 写入 --oas-slider-track-image 渐变覆盖 */
  background-color: var(--oas-slider-track);
  background-image: var(--oas-slider-track-image, none);
}
:host([data-vertical]) .track-wrap::before {
  top: 0;
  bottom: 0;
  left: calc((20px - var(--oas-slider-track-size)) / 2);
  right: auto;
  height: auto;
  width: var(--oas-slider-track-size);
}
:host([disabled]) .track-wrap::before,
:host([data-disabled]) .track-wrap::before {
  opacity: 0.6;
}
/* show-track="false"：轨道条整体退场（灰轨底层 + 选中填充一并隐藏，把手保留可拖/可聚焦） */
:host([data-track='false']) .track-wrap::before,
:host([data-track='false']) .fill {
  display: none;
}
/* 色轨（track 预设）：轨道本身表达色相/明度，隐藏单色填充避免遮挡光谱 */
:host([data-color-track]) .fill {
  display: none;
}
input[type="range"] {
  appearance: none;
  width: 100%;
  height: 20px;
  margin: 0;
  background: transparent;
  cursor: pointer;
  /* 必须定位 + z-index：.fill 与灰轨道（::before）是定位元素，会盖住 static 的
     input——原生 thumb 中部横带被轨道/填充覆盖，圆点右半截「融入」消失（半圆破相）。
     提升 input 后原生 thumb（含底色环）完整压在填充之上；range 模式的
     pointerdown z-index 提升（1/2）也依赖这里的定位基准 */
  position: relative;
  z-index: 1;
}
/* 垂直模式：writing-mode 竖直 + 明确尺寸（input 默认 20px 高，不显式拉满会让
   原生轨道缩在顶部 20px 里，与 200px 轨道区/JS 定位全面错位）。
   原生 thumb 隐藏（opacity 保留命中盒），视觉与拖动由 .custom-thumb + pointer 接管：
   writing-mode 下 Chromium 原生竖直拖拽 hit-test 失效（公认 bug），不可用 */
:host([data-vertical]) input[type="range"] {
  writing-mode: vertical-lr;
  width: 20px;
  height: var(--oas-slider-height, 200px);
}
:host([data-vertical]) .track-wrap {
  touch-action: none;
}
/* range 双滑块：min/max 两个原生 input 必须叠层定位（absolute inset 0 共享 track-wrap 同盒）——
   否则相对定位正常流上下堆叠，max 的拇指掉到轨道线下方（用户实测右侧圆点偏离线条） */
:host([data-range]) input[data-role='range-min'],
:host([data-range]) input[data-role='range-max'] {
  position: absolute;
  inset: 0;
}
/* 多滑块泛化（N≥2）：全部 input 叠层定位共享轨道盒；单值保持正常流 */
:host([data-multi]) input[type='range'] {
  position: absolute;
  inset: 0;
}
.track-inputs,
.thumb-overlays {
  display: contents;
}
:host([data-readonly]) input[type="range"] {
  cursor: default;
}
input::-webkit-slider-runnable-track {
  height: var(--oas-slider-track-size);
  border-radius: var(--oas-slider-track-size);
  background: transparent;
}
input::-webkit-slider-thumb {
  appearance: none;
  filter: var(--oas-glass-refraction, none);
  width: var(--oas-slider-thumb-size);
  height: var(--oas-slider-thumb-size);
  border-radius: 50%;
  /* 垂直居中：Chromium 默认按 margin-box 外缘对齐值点，负 margin 仅影响垂直方向 */
  margin-top: calc((var(--oas-slider-track-size) - var(--oas-slider-thumb-size)) / 2);
  /* 底色「描边」用 box-shadow 实现（不影响盒模型/行程公式）：纯色圆点跨在蓝色填充
     末端时，与填充同色的半圆会融入填充，视觉上像「错位半圆」；外扩底色环让圆点
     在任意值处都完整可读（focus 时由 --oas-focus-ring 整体接管） */
  box-shadow: 0 0 0 2px var(--oas-color-bg);
  background: var(--oas-slider-color);
  border: none;
  transition: transform var(--oas-transition-fast) var(--oas-ease-out);
}
/* 禁用关折射（与 button/switch 同一纪律：禁用控件静态无装饰变形）；
   webkit/moz 伪元素必须分开书写（不认识的选择器会使整条规则失效） */
input:disabled::-webkit-slider-thumb {
  filter: none;
}
input:disabled::-moz-range-thumb {
  filter: none;
}
/* hover 放大只在支持 hover 的设备生效：触屏点按后 :hover 会粘滞，拇指会一直保持放大
   （与纵向自定义拇指共用同一守卫，四种「横向/纵向 × 桌面/触屏」组合行为一致） */
@media (hover: hover) {
  input::-webkit-slider-thumb:hover {
    transform: scale(1.15);
  }
}
/* Firefox：moz 伪元素必须与 webkit 分开书写（浏览器遇到不认识的伪元素会使整条规则失效）；
   ::-moz-range-thumb 相对 track 自动居中，无需 webkit 的 margin-top 偏移 */


input::-moz-range-track {
  height: var(--oas-slider-track-size);
  border-radius: var(--oas-slider-track-size);
  background: transparent;
}
input::-moz-range-thumb {
  width: var(--oas-slider-thumb-size);
  filter: var(--oas-glass-refraction, none);
  height: var(--oas-slider-thumb-size);
  box-sizing: border-box;
  border-radius: 50%;
  background: var(--oas-slider-color);
  border: 2px solid var(--oas-color-bg);
  transition: transform var(--oas-transition-fast) var(--oas-ease-out);
}
@media (hover: hover) {
  input::-moz-range-thumb:hover {
    transform: scale(1.15);
  }
}
/* 自定义滑块/气泡模式：隐藏原生 thumb（保留命中区，拖动与键盘仍走原生输入） */
:host([data-custom-thumb]) input::-webkit-slider-thumb {
  opacity: 0;
}
:host([data-custom-thumb]) input::-moz-range-thumb {
  opacity: 0;
}
/* 垂直模式恒隐藏原生 thumb（命中盒保留）：视觉与拖动由 .custom-thumb + pointer 事件接管 */
:host([data-vertical]) input::-webkit-slider-thumb {
  opacity: 0;
}
:host([data-vertical]) input::-moz-range-thumb {
  opacity: 0;
}
input:focus-visible {
  outline: none;
}
input:focus-visible::-webkit-slider-thumb {
  box-shadow: var(--oas-focus-ring);
}
input:focus-visible::-moz-range-thumb {
  box-shadow: var(--oas-focus-ring);
}
input:disabled {
  cursor: not-allowed;
  opacity: 0.6;
}
/* 已选中区间填充：单值从 start-point（缺省 min）端填充，范围模式填在两 thumb 之间 */
.fill {
  position: absolute;
  top: calc((20px - var(--oas-slider-track-size)) / 2);
  height: var(--oas-slider-track-size);
  border-radius: var(--oas-slider-track-size);
  background: var(--oas-slider-color);
  pointer-events: none;
  transition: left var(--oas-transition-fast) var(--oas-ease-out),
    right var(--oas-transition-fast) var(--oas-ease-out),
    width var(--oas-transition-fast) var(--oas-ease-out);
}
:host([data-vertical]) .fill {
  top: 0;
  left: calc((20px - var(--oas-slider-track-size)) / 2);
  width: var(--oas-slider-track-size);
}
:host([disabled]) .fill,
:host([data-disabled]) .fill {
  opacity: 0.6;
}
/* 自定义滑块：内容可定制（custom-thumb 插槽/模板），与值气泡共存 */
.custom-thumb {
  position: absolute;
  top: 10px;
  left: 0;
  width: var(--oas-slider-thumb-size);
  height: var(--oas-slider-thumb-size);
  box-sizing: border-box;
  display: flex;
  align-items: center;
  justify-content: center;
  transform: translate(-50%, -50%);
  border-radius: 50%;
  pointer-events: none;
  transition: transform var(--oas-transition-fast) var(--oas-ease-out);
  /* 默认实心圆点 + 底环，与原生拇指像素级一致（曾默认空心环导致「圆点 vs 圆圈」不一致；
     仅真正有自定义内容（data-thumb-content）时才回到空心环容器样式） */
  background: var(--oas-slider-color);
  box-shadow: 0 0 0 2px var(--oas-color-bg);
  border: none;
}
/* 自定义内容时的空心环容器（ring：白底彩环，可放内容） */
:host([data-thumb-content]) .custom-thumb {
  background: var(--oas-color-bg);
  border: 2px solid var(--oas-slider-color);
  box-shadow: none;
}
/* thumb=pointer：细指针形态（薄竖条，水平/垂直随轴互换）——置于 data-thumb-content 之后，
   同特异性下后者覆盖，保证 pointer 造型优先于自定义内容环 */
:host([data-thumb-shape='pointer']) .custom-thumb {
  width: 3px;
  height: calc(var(--oas-slider-thumb-size) + 8px);
  border-radius: 2px;
}
:host([data-thumb-shape='pointer'][data-vertical]) .custom-thumb {
  width: calc(var(--oas-slider-thumb-size) + 8px);
  height: 3px;
}
/* 垂直模式：定位原点换到轨道中线 x（top 由 JS 按值写入） */
:host([data-vertical]) .custom-thumb {
  top: 0;
  left: 10px;
}
/* 垂直模式原生 thumb 恒隐藏，.custom-thumb 承担默认拇指视觉（样式已并入上方全局默认，
   此选择器保留仅作语义锚点，无覆盖内容） */
/* author display:flex 会压过 UA [hidden] 规则，显式恢复隐藏（否则 hidden 滑块恒可见：
    默认堆在轨道起点呈白圈、拖动后残留在值位置呈双滑块假象） */
.custom-thumb[hidden] {
  display: none;
}
/* 纵向/自定义拇指的 hover 放大（与横向原生拇指同一守卫）。
   ⚠️ 必须写成 :host(:hover:not([data-readonly]))——在 :host(...) 之后链 :not(...) 的写法在
   Chromium 下整条规则不匹配（实测该规则从未生效，纵向拇指一直没有 hover 反馈）。 */
@media (hover: hover) {
  :host(:hover:not([data-readonly])) .custom-thumb {
    transform: translate(-50%, -50%) scale(1.15);
  }
}
:host([data-focused='value']) .custom-thumb[data-thumb='value'],
:host([data-focused='min']) .custom-thumb[data-thumb='min'],
:host([data-focused='max']) .custom-thumb[data-thumb='max'] {
  box-shadow: var(--oas-focus-ring);
}
/* 多滑块（N>2）：焦点环标记在具体 overlay 上（data-focused 属性由 JS 逐把手同步） */
.custom-thumb[data-focused='true'] {
  box-shadow: var(--oas-focus-ring);
}
.thumb-content {
  display: flex;
  align-items: center;
  justify-content: center;
  max-width: 100%;
  max-height: 100%;
  overflow: hidden;
}
/* 立体声电平表：圆推子下方的双条电平指示（levels 属性提供归一化值；装饰性，不参与交互） */
.levels {
  position: absolute;
  top: calc(100% + 4px);
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  align-items: flex-end;
  gap: 2px;
  height: 18px;
  pointer-events: none;
}
/* author display:flex 会压过 UA [hidden]，无电平值时显式隐藏 */
.levels[hidden] {
  display: none;
}
.level-bar {
  position: relative;
  width: 3px;
  height: 100%;
  overflow: hidden;
  border-radius: 1px;
  background: var(--oas-slider-track);
}
.level-fill {
  position: absolute;
  bottom: 0;
  left: 0;
  width: 100%;
  height: 0%;
  background: var(--oas-slider-color);
  transition: height var(--oas-transition-fast) var(--oas-ease-out);
}
/* 垂直模式：电平表移到圆推子右侧（沿横向排布，避免与下方输入区/刻度重叠） */
:host([data-vertical]) .levels {
  top: 50%;
  left: calc(100% + 4px);
  transform: translateY(-50%);
}
:host([disabled]) .levels,
:host([data-disabled]) .levels {
  opacity: 0.6;
}
/* 值气泡：默认在滑块上方；tooltip-position 四向切换（vertical 默认 right 由 JS 镜像 data-tooltip-pos） */
.thumb-tip {
  position: absolute;
  bottom: calc(100% + 6px);
  left: 50%;
  transform: translateX(-50%);
  padding: 2px 6px;
  border: 1px solid var(--oas-color-border);
  border-radius: var(--oas-radius-sm);
  background: var(--oas-color-bg);
  color: var(--oas-color-text-primary);
  font-size: var(--oas-font-size-xs);
  line-height: 1.4;
  white-space: nowrap;
}
:host([data-tooltip-pos='bottom']) .thumb-tip {
  bottom: auto;
  top: calc(100% + 6px);
}
:host([data-tooltip-pos='left']) .thumb-tip {
  bottom: auto;
  left: auto;
  right: calc(100% + 6px);
  top: 50%;
  transform: translateY(-50%);
}
:host([data-tooltip-pos='right']) .thumb-tip {
  bottom: auto;
  left: calc(100% + 6px);
  top: 50%;
  transform: translateY(-50%);
}
/* 刻度区：紧贴轨道下方（轨道底边距 input 底边 8px，向上偏移使刻度点贴合轨道下缘） */
.marks {
  position: relative;
  width: 100%;
  margin-top: -6px;
}
/* 垂直模式：刻度区移到轨道右侧，刻度点沿竖直轴向排布。
   用 align-self: stretch 拉满轨道高度（height:100% 对弹性项会对着不定高容器解析成 0，
   导致刻度容器塌缩、所有刻度点堆在顶部） */
:host([data-vertical]) .marks {
  width: auto;
  height: auto;
  align-self: stretch;
  margin-top: 0;
  margin-inline-start: var(--oas-space-2);
}
.mark {
  position: absolute;
  top: 0;
  transform: translateX(-50%);
}
:host([data-vertical]) .mark {
  display: flex;
  align-items: center;
  transform: translateY(-50%);
}
.mark-dot {
  width: 4px;
  height: 4px;
  margin: 0 auto;
  border-radius: 50%;
  background: var(--oas-color-border);
}
.mark[data-passed='true'] .mark-dot {
  background: var(--oas-slider-color);
}
.mark-label {
  margin-top: var(--oas-space-1);
  font-size: var(--oas-font-size-xs);
  line-height: 1.4;
  color: var(--oas-color-text-secondary);
  white-space: nowrap;
}
:host([data-vertical]) .mark-label {
  margin-top: 0;
  margin-inline-start: var(--oas-space-1);
}
.mark[data-passed='true'] .mark-label {
  color: var(--oas-color-text-primary);
}
:host([disabled]) .marks,
:host([data-disabled]) .marks {
  opacity: 0.6;
}
/* 数值输入区（show-input） */
.inputs {
  display: flex;
  align-items: center;
  gap: var(--oas-space-1);
  flex: 0 0 auto;
}
:host([data-vertical]) .inputs {
  margin-top: var(--oas-space-2);
}
/* 同类保护：无 show-input 时 inputs 整区 hidden，display:flex 不得压过 UA [hidden]
   （否则空容器仍占 wrap 的 flex gap，轨道右侧多出一段间距） */
.inputs[hidden] {
  display: none;
}
.inputs input {
  appearance: none;
  box-sizing: border-box;
  /* 读数宽度开口（value-width 属性写入 --oas-slider-value-width；缺省 72px） */
  width: var(--oas-slider-value-width, 72px);
  height: var(--oas-control-height-md);
  padding: 0 var(--oas-space-2);
  border: 1px solid var(--oas-color-border);
  border-radius: var(--oas-radius-md);
  background: var(--oas-color-bg);
  color: var(--oas-color-text-primary);
  font-size: var(--oas-font-size-md);
  font-family: inherit;
  text-align: center;
  transition: border-color var(--oas-transition-fast) var(--oas-ease-out),
    box-shadow var(--oas-transition-fast) var(--oas-ease-out);
  /* 触屏放行纵向滚动：水平位移让给读数 scrub（pointer 接管），竖直滑动不困在输入框 */
  touch-action: pan-y;
}
.inputs input:hover {
  border-color: var(--oas-color-primary);
}
.inputs input:focus {
  outline: none;
  border-color: var(--oas-color-primary);
  box-shadow: var(--oas-focus-ring);
}
.inputs input:disabled {
  cursor: not-allowed;
  background: var(--oas-color-bg-disabled);
  color: var(--oas-color-text-disabled);
}
.inputs input[readonly] {
  cursor: default;
}
/* scrub 拖动中：ew-resize 指示横向微调；强调色走 accent-color（缺省主题主色） */
.inputs input[data-scrubbing] {
  cursor: ew-resize;
  border-color: var(--oas-slider-accent, var(--oas-color-primary));
  color: var(--oas-slider-accent, var(--oas-color-text-primary));
  user-select: none;
}
/* 隐藏原生步进箭头，与滑块数值展示更干净 */
.inputs input::-webkit-outer-spin-button,
.inputs input::-webkit-inner-spin-button {
  -webkit-appearance: none;
  margin: 0;
}
.inputs input[type='number'] {
  -moz-appearance: textfield;
  appearance: textfield;
}
.input-sep {
  color: var(--oas-color-text-secondary);
  user-select: none;
}
`

export class OASSlider extends OASElement {
  static override get observedAttributes(): string[] {
    return [
      'value',
      'min',
      'max',
      'step',
      'disabled',
      'marks',
      'range',
      'show-input',
      'reverse',
      'show-tooltip',
      'disabled-skip',
      'vertical',
      'format',
      'tooltip-always',
      'tooltip-position',
      'show-stops',
      'start-point',
      'size',
      'color',
      'track-color',
      'readonly',
      'large-step',
      'label',
      'labels',
      // 专业密度批：数值 scrub 开关（默认开，"false" 关）+ 双击复位 + 专业属性
      'scrub',
      'reset-value',
      'precision',
      'value-width',
      'accent-color',
      'show-track',
      // 增强批：thumb 两形态（pointer 细指针 / round 圆推子）+ 立体声电平表 + 色轨
      'thumb',
      'levels',
      'track',
      'label-1',
      'label-2',
      'label-3',
      'label-4',
      'label-5',
      'label-6',
      'label-7',
      'label-8',
    ]
  }

  /**
   * @apiProperty 当前值：单把手为数值，多把手（range / `value` 数组）为数值数组。
   * 已渲染读各把手实时值（拖动中即时反映）；未渲染回落解析 `value` 属性。
   */
  get value(): number | number[] {
    if (this.thumbs.length) {
      return this.isMulti() ? this.currentValues() : Number(this.thumbs[0]?.value ?? 0)
    }
    const arr = parseValueArray(this.getAttr('value', ''))
    if (arr) return arr.slice()
    return Number(this.getAttr('value', '0')) || 0
  }

  /**
   * 程序性写值（受控赋值即生效语义）：写受控 `value` 属性（数组走 JSON）并强制 update
   * 重建/回显把手与数值框，不派发任何事件。同值 `setAttribute` 不触发
   * attributeChangedCallback，显式 update 保证生效。
   */
  set value(v: number | number[]) {
    const attr = Array.isArray(v) ? JSON.stringify(v.map((n) => Number(n))) : String(Number(v) || 0)
    this.setAttribute('value', attr)
    if (this.hasRendered) this.update()
  }

  /** 动态把手（原生 range input）：索引即把手序号，N 由 value 数组长度/range 决定 */
  private thumbs: HTMLInputElement[] = []
  /** 每个把手的自定义视觉层（.custom-thumb，含值气泡） */
  private overlays: HTMLElement[] = []
  /** show-input 数值输入框（与把手一一对应） */
  private numInputs: HTMLInputElement[] = []
  /** 数值输入框之间的分隔符（多值时显示） */
  private numSeps: HTMLSpanElement[] = []
  private wrap: HTMLElement | null = null
  /** 本次渲染生效的把手数（事件回调里判定索引用） */
  private thumbCountCache = 1
  /**
   * 兼容占位（隐藏、不可交互、非把手）：保持历史契约里「非当前模式的槽位以 hidden 存在于 DOM」——
   * range 模式仍能查到隐藏的单值 `[data-role="range"]`/`[data-role="num"]`。位于真实把手之后，
   * 因此单值模式下 `querySelector('[data-role="range"]')` 命中的仍是真实把手。
   */
  private legacyRangeSlot: HTMLInputElement | null = null
  private legacyNumSlot: HTMLInputElement | null = null
  /** 上次渲染的刻度签名（min/max + step + reverse + vertical + 条目），用于增量重建判断 */
  private marksKey = ''
  private debounceTimer: ReturnType<typeof setTimeout> | null = null
  private thumbObserver: MutationObserver | null = null
  /** 拖动中（用于拖动时显示值气泡，不依赖 show-tooltip） */
  private dragging = false
  /** 垂直拖动中的 pointerId（pointer 接管拖动的存活标记；null = 未在拖） */
  private verticalDragId: number | null = null
  /** 读数区 scrub 状态（null = 未在 scrub；单指针独占，第二指按下忽略） */
  private numScrub: {
    id: number
    anchor: ScrubAnchor
    index: number
    active: boolean
    startValue: number
  } | null = null
  /** tooltip 格式化函数（JS property 通道，优先于 format 模板串） */
  private _formatTooltip: ((value: number) => string | number | null | undefined) | null = null

  /** 宿主框架（Vue/React）以对象/数组赋值时走 property setter，反射到 attribute 统一解析链路 */
  get marks(): string {
    return this.getAttribute('marks') ?? ''
  }
  set marks(value: string | Record<string, string | number> | number[]) {
    this.setAttribute('marks', typeof value === 'string' ? value : JSON.stringify(value))
  }

  /**
   * @apiProperty 立体声电平表（立体声推子下方的双条电平指示）：`{ left, right }` 归一化值（0–1）。
   * 属性通道为 JSON 字符串；宿主框架可用对象直接赋值（反射为 JSON attribute）。
   * 仅 `thumb="round"`（默认）渲染；非法/空值隐藏电平表。
   */
  get levels(): { left: number; right: number } | null {
    return this.parseLevels()
  }
  set levels(value: { left?: number; right?: number } | string | null) {
    if (value == null || value === '') {
      this.removeAttribute('levels')
    } else {
      this.setAttribute('levels', typeof value === 'string' ? value : JSON.stringify(value))
    }
    if (this.hasRendered) this.update()
  }

  /**
   * 值格式化函数（函数通道）：输出同时进值气泡与 aria-valuetext。
   * 优先级：formatTooltip > format 模板串 > 裸数字。置 null 清除。
   */
  get formatTooltip(): ((value: number) => string | number | null | undefined) | null {
    return this._formatTooltip
  }
  set formatTooltip(fn: ((value: number) => string | number | null | undefined) | null) {
    this._formatTooltip = typeof fn === 'function' ? fn : null
    if (this.hasRendered) this.update()
  }

  /** 纯函数：SSR 快照与客户端渲染共用同一份模板，保证两路径结构严格一致 */
  private template(): string {
    return `
      <style>${STYLE}</style>
      <div class="wrap" part="wrap">
        <div class="slider-col" part="slider">
          <div class="track-wrap" part="track-wrap">
            <div class="track-inputs" part="track-inputs"></div>
            <div class="fill" part="fill"></div>
            <div class="thumb-overlays" part="thumb-overlays"></div>
          </div>
          <div class="marks" part="marks" hidden></div>
        </div>
        <div class="inputs" part="inputs" hidden></div>
      </div>
    `
  }

  /** 缓存节点引用 + 绑定轨道级事件（render 与水合路径共用；把手级监听在创建时挂） */
  private bind(): void {
    const wrap = this.shadow.querySelector<HTMLElement>('[part="track-wrap"]')
    this.wrap = wrap

    // 轨道指针按下：只读拦截；垂直模式 pointer 接管；多把手按「离哪个拇指近」提升该 input 的 z-index
    wrap?.addEventListener('pointerdown', (e) => {
      if (this.hasAttr('readonly')) {
        e.preventDefault()
        return
      }
      // 垂直模式：原生竖直拖拽 hit-test 失效，改由 pointer 事件接管（点击轨道即定位、拖动跟随）
      if (this.hasAttr('vertical') && wrap) {
        if (this.injectDisabled()) return
        this.beginVerticalDrag(e as PointerEvent, wrap)
        return
      }
      if (!this.isMulti() || !wrap) return
      const rect = wrap.getBoundingClientRect()
      if (!rect.width) return
      const min = Number(this.getAttr('min', '0'))
      const max = Number(this.getAttr('max', '100'))
      const pct = ((e as PointerEvent).clientX - rect.left) / rect.width
      const reverse = this.horizontalReverse()
      const valueAt = min + (reverse ? 1 - pct : pct) * (max - min)
      this.raiseThumb(this.nearestThumbIndex(valueAt))
    })

    // 双击轨道复位（reset-value，缺省回落 min）：单值模式响应，多把手/禁用/只读拦截
    wrap?.addEventListener('dblclick', () => this.resetToValue())

    // 键盘/焦点：聚焦哪个把手置顶 + 焦点环映射到自定义视觉层（聚焦时显示值气泡）
    wrap?.addEventListener('focusin', (e) => {
      const i = this.thumbs.indexOf(e.target as HTMLInputElement)
      if (i < 0 || i >= this.thumbCountCache) {
        this.removeAttribute('data-focused')
        this.setOverlayFocus(-1)
        this.syncOverlay()
        return
      }
      const n = this.thumbCountCache
      const key = n === 1 ? 'value' : n === 2 ? (i === 0 ? 'min' : 'max') : String(i)
      this.setAttribute('data-focused', key)
      this.setOverlayFocus(i)
      if (this.isMulti()) this.raiseThumb(i)
      this.syncOverlay()
    })
    wrap?.addEventListener('focusout', () => {
      this.removeAttribute('data-focused')
      this.setOverlayFocus(-1)
      this.syncOverlay()
    })

    // 自定义滑块内容（light DOM 插槽/模板）变化 → 重同步
    this.thumbObserver = new MutationObserver(() => {
      this.syncThumbContent()
      this.syncOverlay()
    })
    this.thumbObserver.observe(this, { childList: true })
    // 轨道尺寸变化（响应式/折叠）→ 重算自定义滑块像素位置
    if (wrap && typeof ResizeObserver !== 'undefined') {
      const ro = new ResizeObserver(() => this.syncOverlay())
      ro.observe(wrap)
      this.onCleanup(() => ro.disconnect())
    }
    // 防抖计时器随断开清理（observer 随元素同生命周期，断开不失效）
    this.onCleanup(() => this.cancelDebounce())
    // 读数 scrub 的 window 级监听随断连清理 + 复位 scrub 状态——否则断连重连后 numScrub
    // 残留（beginNumScrub 早退守卫恒真）会让读数 scrub 永久失效（无孤儿监听/无悬挂态）
    this.onCleanup(() => {
      this.numScrub = null
      this.finishNumScrubListeners()
    })
    this.ensureLegacySlots()
  }

  protected override render(): void {
    this.shadow.innerHTML = this.template()
    this.bind()
    this.update()
  }

  /** 真水合：校验 SSR 快照结构（轨道容器存在）后直接接管，跳过 shadow 重建 */
  protected override hydrate(): boolean {
    if (!this.shadow.querySelector('[part="track-wrap"]')) return false
    this.bind()
    return true
  }

  protected override update(): void {
    const wrap = this.shadow.querySelector<HTMLElement>('[part="track-wrap"]')
    this.wrap = wrap
    if (!wrap) return
    const n = this.thumbCount()
    this.thumbCountCache = n
    this.ensureThumbElements(n)
    this.ensureNumElements(n)

    const showInput = this.hasAttr('show-input')
    const reverse = this.hasAttr('reverse')
    const vertical = this.hasAttr('vertical')
    const readonly = this.hasAttr('readonly')
    // disabled 就近读取全局禁用注入（组件显式 disabled > 豁免 > provider 注入）
    const disabled = this.injectDisabled()
    // 镜像最终禁用态到宿主 data-disabled（供 :host([data-disabled]) 样式消费，覆盖注入场景）
    this.toggleAttribute('data-disabled', disabled)
    const min = Number(this.getAttr('min', '0'))
    const max = Number(this.getAttr('max', '100'))
    const markMode = this.isMarkStep()
    const multi = n >= 2
    // 原生 step：mark 模式给 any（吸附由组件接管），无 marks 时回退 1
    const step = this.getAttr('step', '1') === 'mark' ? (markMode ? 'any' : '1') : this.getAttr('step', '1')
    // 方向：horizontal 有效反转（reverse 属性 XOR RTL 书写方向）时 min 在右（dir=rtl 由原生 range 镜像）；
    // vertical 默认 min 在下（rtl），reverse 镜像到上（vertical 不随书写方向镜像）
    const dir = vertical ? (reverse ? 'ltr' : 'rtl') : this.horizontalReverse() ? 'rtl' : 'ltr'

    // 宿主状态镜像（data-* 非 observed 属性，写入不触发 attributeChangedCallback 循环）
    this.toggleAttribute('data-vertical', vertical)
    this.toggleAttribute('data-readonly', readonly)
    this.toggleAttribute('data-range', this.hasAttr('range'))
    this.toggleAttribute('data-multi', multi)
    this.setAttribute('data-size', this.normalizeSize())
    this.setAttribute('data-thumb-shape', this.normalizeThumb())
    this.setAttribute('data-tooltip-pos', this.tooltipPosition(vertical))
    this.applyHostVar('color', '--oas-slider-color')
    this.applyHostVar('track-color', '--oas-slider-track')
    this.applyHostVar('accent-color', '--oas-slider-accent')
    // 色轨（track 预设）：JS 组装方向感知的 CSS 渐变写入宿主变量，CSS 由 .track-wrap::before 消费；
    // 色轨激活时隐藏单色填充（光谱自表达，避免遮挡）
    const trackImage = this.colorTrackImage()
    if (trackImage) {
      this.style.setProperty('--oas-slider-track-image', trackImage)
      this.setAttribute('data-color-track', '')
    } else {
      this.style.removeProperty('--oas-slider-track-image')
      this.removeAttribute('data-color-track')
    }
    // show-track 轨道显隐开关：仅显式 "false" 时镜像 data-track=false（CSS 消费）；
    // 未设置时不写 data-*（不给宿主叠加属性、SSR 快照属性序列零漂移）
    if (this.getAttr('show-track', 'true') === 'false') this.setAttribute('data-track', 'false')
    else this.removeAttribute('data-track')
    // value-width 读数宽度：纯数字补 px，带单位值原样透传（与 --oas-slider-height 同款约定）
    const valueWidth = this.getAttr('value-width', '').trim()
    if (valueWidth === '') {
      this.style.removeProperty('--oas-slider-value-width')
    } else {
      const n = Number(valueWidth)
      this.style.setProperty('--oas-slider-value-width', Number.isFinite(n) ? `${n}px` : valueWidth)
    }

    // 受控值：多把手为数组（排序/夹取/刻度吸附），单把手沿用 value 字符串
    const values = multi ? this.orderedValues(n, min, max, markMode) : null

    for (let i = 0; i < this.thumbs.length; i++) {
      const r = this.thumbs[i]!
      const active = i < n
      r.hidden = !active
      if (!active) continue
      r.dataset.role = this.roleFor(i, n)
      r.min = String(min)
      r.max = String(max)
      r.step = step
      r.disabled = disabled
      r.readOnly = readonly
      r.setAttribute('dir', dir)
      r.setAttribute('role', 'slider')
      if (vertical) {
        // orient 属性兼容 Firefox 竖直渲染；writing-mode 走 CSS（现代方案）
        r.setAttribute('orient', 'vertical')
        r.setAttribute('aria-orientation', 'vertical')
      } else {
        r.removeAttribute('orient')
        r.removeAttribute('aria-orientation')
      }
      if (readonly) r.setAttribute('aria-readonly', 'true')
      else r.removeAttribute('aria-readonly')
      r.setAttribute('aria-label', this.labelFor(i, n))
      if (values) {
        r.value = String(values[i]!)
      } else {
        const raw = this.getAttr('value', '')
        const shown = raw === '' ? '' : markMode ? String(this.snapToMark(Number(raw))) : raw
        if (r.value !== shown) r.value = shown
      }
      r.setAttribute('aria-valuenow', r.value)
    }

    // show-input 数值输入框（一一对应把手）
    const numValues = multi ? this.currentValues() : null
    for (let i = 0; i < this.numInputs.length; i++) {
      const input = this.numInputs[i]!
      const active = i < n
      input.hidden = !showInput || !active
      if (!active) continue
      input.dataset.role = this.numRoleFor(i, n)
      input.disabled = disabled
      input.readOnly = readonly
      input.setAttribute('aria-label', this.labelFor(i, n))
      if (numValues) input.value = String(numValues[i]!)
      else input.value = String(Number(this.thumbs[0]?.value ?? 0))
    }
    const inputsEl = this.shadow.querySelector<HTMLElement>('[part="inputs"]')
    if (inputsEl) inputsEl.hidden = !showInput
    for (let i = 0; i < this.numSeps.length; i++) {
      this.numSeps[i]!.hidden = !showInput || !multi || i >= n - 1
    }

    this.syncThumbContent()
    this.syncOverlay()
    this.syncMarks()
    this.ensureLegacySlots()
  }

  /** label 点击聚焦委托：把焦点交给 shadow 内主滑块（配合 oas-form-item 的 label 点击代理） */
  override focus(options?: FocusOptions): void {
    this.thumbs[0]?.focus(options)
  }

  /** 兼容占位：在真实把手/输入框之后追加隐藏、不可聚焦的旧契约槽位（range 模式的单值槽位） */
  private ensureLegacySlots(): void {
    const inputs = this.shadow.querySelector<HTMLElement>('[part="track-inputs"]')
    if (inputs) {
      if (!this.legacyRangeSlot) {
        const s = document.createElement('input')
        s.type = 'range'
        s.setAttribute('data-role', 'range')
        s.setAttribute('aria-hidden', 'true')
        s.tabIndex = -1
        this.legacyRangeSlot = s
      }
      this.legacyRangeSlot.hidden = true
      inputs.appendChild(this.legacyRangeSlot)
    }
    const nums = this.shadow.querySelector<HTMLElement>('[part="inputs"]')
    if (nums) {
      if (!this.legacyNumSlot) {
        const s = document.createElement('input')
        s.type = 'number'
        s.setAttribute('data-role', 'num')
        s.setAttribute('aria-hidden', 'true')
        s.tabIndex = -1
        this.legacyNumSlot = s
      }
      this.legacyNumSlot.hidden = true
      nums.appendChild(this.legacyNumSlot)
    }
  }

  // ---------- 把手结构（动态 N） ----------

  /** 生效把手数：value 数组长度（≥2）优先；否则 range 属性 → 2；其余 1 */
  private thumbCount(): number {
    const arr = parseValueArray(this.getAttr('value', ''))
    if (arr) return arr.length
    return this.hasAttr('range') ? 2 : 1
  }

  private isMulti(): boolean {
    return this.thumbCountCache >= 2
  }

  private activeThumbCount(): number {
    return this.thumbCountCache
  }

  /** 把手 data-role：单值 range；range 双值 range-min/range-max（既有契约）；N≥3 thumb-<i> */
  private roleFor(i: number, n: number): string {
    if (n === 1) return 'range'
    if (n === 2 && this.hasAttr('range')) return i === 0 ? 'range-min' : 'range-max'
    return `thumb-${i}`
  }

  /** 数值输入框 data-role：单值 num；range 双值 num-min/num-max（既有契约）；N≥3 num-<i> */
  private numRoleFor(i: number, n: number): string {
    if (n === 1) return 'num'
    if (n === 2 && this.hasAttr('range')) return i === 0 ? 'num-min' : 'num-max'
    return `num-${i}`
  }

  /** 逐把手可访问名：label-<i+1> 属性 > labels JSON 数组 > label 基准（range 双值加最小值/最大值后缀；N≥3 加序号） */
  private labelFor(i: number, n: number): string {
    const per = this.getAttr(`label-${i + 1}`, '')
    if (per !== '') return per
    const labels = this.parseLabels()
    if (labels && labels[i] != null && labels[i] !== '') return labels[i]!
    const base = this.getAttr('label', '')
    if (n === 1) return base || this.t('slider.valueLabel')
    if (n === 2) {
      if (base) return i === 0 ? `${base} ${this.t('slider.minLabel')}` : `${base} ${this.t('slider.maxLabel')}`
      return i === 0 ? this.t('slider.minLabel') : this.t('slider.maxLabel')
    }
    return base ? `${base} ${i + 1}` : `${this.t('slider.valueLabel')} ${i + 1}`
  }

  /** 解析立体声电平值 `{ left, right }`（归一化到 [0,1]）；非法/缺失返回 null */
  private parseLevels(): { left: number; right: number } | null {
    const raw = this.getAttr('levels', '')
    if (!raw) return null
    let parsed: unknown
    try {
      parsed = JSON.parse(raw)
    } catch {
      return null
    }
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return null
    const o = parsed as Record<string, unknown>
    const l = Number(o.left)
    const r = Number(o.right)
    if (!Number.isFinite(l) && !Number.isFinite(r)) return null
    return { left: clampNum(Number.isFinite(l) ? l : 0, 0, 1), right: clampNum(Number.isFinite(r) ? r : 0, 0, 1) }
  }

  /** labels JSON 数组（多把手可访问名），非法/非数组返回 null */
  private parseLabels(): string[] | null {
    const raw = this.getAttr('labels', '')
    if (!raw) return null
    try {
      const parsed: unknown = JSON.parse(raw)
      if (Array.isArray(parsed) && parsed.every((x) => typeof x === 'string')) return parsed as string[]
    } catch {
      /* 非法 JSON 视为未配置 */
    }
    return null
  }

  /** 懒创建把手 input + 自定义视觉层（复用已有节点，只增不减；多余把手由 update 隐藏） */
  private ensureThumbElements(n: number): void {
    const inputs = this.shadow.querySelector<HTMLElement>('[part="track-inputs"]')
    const overlays = this.shadow.querySelector<HTMLElement>('[part="thumb-overlays"]')
    if (!inputs || !overlays) return
    while (this.thumbs.length < n) {
      const i = this.thumbs.length
      const r = document.createElement('input')
      r.type = 'range'
      r.setAttribute('part', 'track')
      r.dataset.role = 'range'
      r.addEventListener('input', () => this.onThumbInput(i, false))
      r.addEventListener('change', () => this.onThumbInput(i, true))
      r.addEventListener('keydown', (e) => this.handleKeydown(e as KeyboardEvent))
      inputs.appendChild(r)
      this.thumbs.push(r)

      const ov = document.createElement('div')
      ov.className = 'custom-thumb'
      ov.setAttribute('part', 'thumb')
      ov.setAttribute('data-thumb', 'value')
      ov.hidden = true
      const content = document.createElement('div')
      content.className = 'thumb-content'
      const tip = document.createElement('div')
      tip.className = 'thumb-tip'
      tip.setAttribute('part', 'tip')
      // 立体声电平表：圆推子下方的双条电平指示（值由 syncOverlay 写入，装饰性 aria-hidden）
      const levels = document.createElement('div')
      levels.className = 'levels'
      levels.setAttribute('part', 'levels')
      levels.setAttribute('aria-hidden', 'true')
      levels.hidden = true
      for (const channel of ['left', 'right'] as const) {
        const bar = document.createElement('span')
        bar.className = 'level-bar'
        bar.setAttribute('data-channel', channel)
        const fill = document.createElement('span')
        fill.className = 'level-fill'
        bar.appendChild(fill)
        levels.appendChild(bar)
      }
      ov.append(content, tip, levels)
      overlays.appendChild(ov)
      this.overlays.push(ov)
    }
  }

  /** 懒创建数值输入框 + 分隔符（一一对应把手） */
  private ensureNumElements(n: number): void {
    const box = this.shadow.querySelector<HTMLElement>('[part="inputs"]')
    if (!box) return
    while (this.numInputs.length < n) {
      const i = this.numInputs.length
      if (i > 0) {
        const sep = document.createElement('span')
        sep.className = 'input-sep'
        sep.setAttribute('part', 'input-sep')
        sep.textContent = '–'
        box.appendChild(sep)
        this.numSeps.push(sep)
      }
      const input = document.createElement('input')
      input.type = 'number'
      input.setAttribute('part', 'input')
      input.dataset.role = 'num'
      input.addEventListener('input', () => this.scheduleCommit(i))
      input.addEventListener('change', () => {
        this.cancelDebounce()
        this.commitFromNumber(i, true)
      })
      // 读数区 scrub（像素锚定计步）：按下记录锚点，横移 ≥1 个计步单位激活
      input.addEventListener('pointerdown', (e) => this.beginNumScrub(i, e as PointerEvent))
      box.appendChild(input)
      this.numInputs.push(input)
    }
  }

  /** 受控值解析（多把手）：数组 → 逐个刻度吸附/夹取 → 升序 */
  private orderedValues(n: number, min: number, max: number, markMode: boolean): number[] {
    const raw = parseValueArray(this.getAttr('value', ''))
    const base = raw && raw.length > 0 ? raw : this.hasAttr('range') ? [min, max] : [min, max]
    const out: number[] = []
    for (let i = 0; i < n; i++) {
      let v = base[i] ?? (i === 0 ? min : max)
      if (!Number.isFinite(v)) v = i === 0 ? min : max
      if (markMode) v = this.snapToMark(v)
      out.push(clampNum(v, min, max))
    }
    out.sort((a, b) => a - b)
    return out
  }

  /** 读取各把手实时值（拖动态，不读属性），夹取到 [min,max] */
  private currentValues(): number[] {
    const n = this.activeThumbCount()
    const min = Number(this.getAttr('min', '0'))
    const max = Number(this.getAttr('max', '100'))
    const out: number[] = []
    for (let i = 0; i < n; i++) {
      out.push(clampNum(Number(this.thumbs[i]?.value ?? min), min, max))
    }
    return out
  }

  /** 事件 detail 值：单把手为数值，多把手为数组 */
  private emitValue(): number | number[] {
    if (!this.isMulti()) return Number(this.thumbs[0]?.value ?? 0)
    return this.currentValues()
  }

  /** 把手 input/change 统一链路（拖动吸附 → 排序约束 → 全量同步 → 派发） */
  private onThumbInput(i: number, commit: boolean): void {
    if (i >= this.thumbCountCache) return
    this.dragging = !commit
    this.maybeSnapInput(this.thumbs[i] ?? null)
    this.enforceOrder(i)
    this.syncValueAttr()
    this.syncOverlay()
    this.syncNumInputs()
    this.syncMarkPassed()
    this.emit(commit ? 'change' : 'input', { value: this.emitValue() })
  }

  /** 拖动排序约束：单把手无约束；range 属性双值交换保持 lo ≤ hi（既有契约）；value 数组模式（含 N=2 非 range）与 N≥3 统一夹取相邻把手之间防穿越 */
  private enforceOrder(i: number): void {
    const n = this.activeThumbCount()
    if (n < 2) return
    // range 属性保留交换旧契约（lo/hi 超界交换）；value 数组模式（含 N=2）统一夹取相邻把手之间
    if (n === 2 && this.hasAttr('range')) {
      this.clampRangeInputs()
      return
    }
    const min = Number(this.getAttr('min', '0'))
    const max = Number(this.getAttr('max', '100'))
    const target = this.thumbs[i]
    if (!target) return
    const lo = i > 0 ? clampNum(Number(this.thumbs[i - 1]?.value ?? min), min, max) : min
    const hi = i < n - 1 ? clampNum(Number(this.thumbs[i + 1]?.value ?? max), min, max) : max
    target.value = String(clampNum(Number(target.value) || 0, lo, hi))
  }

  /** 提升指定把手 input 的 z-index（多把手叠层时抢拖动/命中） */
  private raiseThumb(idx: number): void {
    for (let i = 0; i < this.thumbs.length; i++) {
      this.thumbs[i]!.style.zIndex = i === idx ? '2' : '1'
    }
  }

  /** 距给定值最近的把手索引 */
  private nearestThumbIndex(v: number): number {
    const vals = this.currentValues()
    let best = 0
    let bestD = Infinity
    for (let i = 0; i < vals.length; i++) {
      const d = Math.abs(vals[i]! - v)
      if (d < bestD) {
        bestD = d
        best = i
      }
    }
    return best
  }

  /** 焦点环标记到指定自定义视觉层（-1 清除） */
  private setOverlayFocus(idx: number): void {
    for (let i = 0; i < this.overlays.length; i++) {
      if (i === idx) this.overlays[i]!.setAttribute('data-focused', 'true')
      else this.overlays[i]!.removeAttribute('data-focused')
    }
  }

  // ---------- 属性归一 / 格式化 ----------

  /** size 三档归一：接受 sm/md/lg 与 small/medium/large 两套词表（config-provider 注入同构），非法回落 md */
  private normalizeSize(): 'sm' | 'md' | 'lg' {
    const raw = this.injectValue('size', 'medium').toLowerCase()
    if (raw === 'sm' || raw === 'small') return 'sm'
    if (raw === 'lg' || raw === 'large') return 'lg'
    return 'md'
  }

  /** thumb 两形态归一：pointer=细指针轨（薄条）、round=粗圆推子（默认），非法/缺省回落 round */
  private normalizeThumb(): 'pointer' | 'round' {
    return this.getAttr('thumb', 'round').toLowerCase() === 'pointer' ? 'pointer' : 'round'
  }

  /** 当前 thumb 是否为细指针形态（pointer 恒走自定义视觉层，原生拇指隐藏） */
  private isPointerThumb(): boolean {
    return this.normalizeThumb() === 'pointer'
  }

  /** 生效的气泡方向：显式属性（四向）优先，vertical 默认 right、水平默认 top */
  private tooltipPosition(vertical: boolean): string {
    const raw = this.getAttr('tooltip-position', '')
    if (TOOLTIP_POSITIONS.has(raw)) return raw
    return vertical ? 'right' : 'top'
  }

  /** color/track-color 属性 → 宿主 CSS 变量（预设语义色映射 token，任意值原样透传） */
  private applyHostVar(attr: string, cssVar: string): void {
    const raw = this.getAttr(attr, '')
    if (raw === '') {
      this.style.removeProperty(cssVar)
      return
    }
    this.style.setProperty(cssVar, COLOR_TOKENS[raw] ?? raw)
  }

  /**
   * 色轨渐变组装（track 预设）：hue=色相光谱、saturation=轨道色→推子色、luminance=黑→白、
   * gradient=轨道色→推子色 两段 CSS 渐变。方向随轴与反转感知（水平 min 在左 / 垂直 min 在下，
   * reverse 与 RTL 镜像）。非预设/未设置返回 null（回落纯色轨道）。
   * 光谱端点无法用语义 token 表达（hue/luminance 为绝对色相/明度），沿用组件内颜色字面量约定。
   */
  private colorTrackImage(): string | null {
    const kind = this.getAttr('track', '').toLowerCase()
    if (!kind) return null
    const vertical = this.hasAttr('vertical')
    const flip = vertical ? this.hasAttr('reverse') : this.horizontalReverse()
    const dir = vertical ? (flip ? 'to bottom' : 'to top') : flip ? 'to left' : 'to right'
    switch (kind) {
      case 'hue':
        return `linear-gradient(${dir}, #ff0000, #ffff00, #00ff00, #00ffff, #0000ff, #ff00ff, #ff0000)`
      case 'saturation':
        return `linear-gradient(${dir}, var(--oas-color-border-strong), var(--oas-slider-color))`
      case 'luminance':
        return `linear-gradient(${dir}, #000000, #ffffff)`
      case 'gradient':
        return `linear-gradient(${dir}, var(--oas-slider-track), var(--oas-slider-color))`
      default:
        return null
    }
  }

  /** step="mark" 模式生效判定：step 属性为字面量 mark 且 marks 非空 */
  private isMarkStep(): boolean {
    return this.getAttr('step', '1') === 'mark' && this.parseMarks().length > 0
  }

  /** 吸附到最近刻度（距离并列时取较小值，保守不越界） */
  private snapToMark(v: number): number {
    const marks = this.parseMarks()
    if (marks.length === 0) return v
    let best = marks[0]!.value
    let bestD = Infinity
    for (const m of marks) {
      const d = Math.abs(m.value - v)
      if (d < bestD) {
        bestD = d
        best = m.value
      }
    }
    return best
  }

  /** 拖动/受控同步时把输入值吸附到刻度（step="mark" 模式） */
  private maybeSnapInput(r: HTMLInputElement | null): void {
    if (!r || !this.isMarkStep()) return
    r.value = String(this.snapToMark(Number(r.value)))
  }

  /** 当前 thumb 直径（像素换算用；与 CSS --oas-slider-thumb-size 各档成对维护） */
  private thumbSize(): number {
    return THUMB_SIZES[this.getAttribute('data-size') ?? 'md'] ?? THUMB_SIZE
  }

  /** 格式化值（值气泡 + aria-valuetext 双通道）：函数 property 优先于 format 模板串；
   *  都未配置时按 precision 修约显示（收敛步进浮点尾巴，如 0.30000000000000004 → 0.3） */
  private formatValue(v: number): string {
    const fn = this._formatTooltip
    if (typeof fn === 'function') {
      const r = fn(v)
      return r == null ? String(v) : String(r)
    }
    const tpl = this.getAttr('format', '')
    if (tpl) return tpl.replaceAll('${value}', String(v))
    const precision = this.precisionAttr()
    if (precision != null) return String(roundToPrecision(v, precision))
    return String(v)
  }

  private hasFormatter(): boolean {
    return (
      typeof this._formatTooltip === 'function' || this.getAttr('format', '') !== '' || this.precisionAttr() != null
    )
  }

  /** 解析 start-point（夹取到 [min, max]；未设/非法回落 min = 从最小端填充） */
  private startPointValue(min: number, max: number): number {
    const raw = this.getAttr('start-point', '')
    if (raw === '') return min
    const v = Number(raw)
    if (!Number.isFinite(v)) return min
    return clampNum(v, min, max)
  }

  // ---------- 键盘 ----------

  /**
   * 键盘拦截（统一大步进，抹平浏览器原生差异）：
   * - 只读：拦截一切值键（不可改值、不派发事件，保留可聚焦）
   * - step="mark"：方向键/页键/Home/End 全部接管为刻度档位跳转
   * - 普通步长：仅拦截大步键（Shift+方向 / PageUp / PageDown，步进量 = large-step 或 10×step）；
   *   普通方向键/Home/End 保留原生行为
   */
  private handleKeydown(e: KeyboardEvent): void {
    if (this.injectDisabled()) return
    const key = e.key
    const isArrow = key === 'ArrowLeft' || key === 'ArrowRight' || key === 'ArrowUp' || key === 'ArrowDown'
    const isPage = key === 'PageUp' || key === 'PageDown'
    const isBound = key === 'Home' || key === 'End'
    if (!isArrow && !isPage && !isBound) return
    if (this.hasAttr('readonly')) {
      e.preventDefault()
      return
    }
    const input = e.target as HTMLInputElement
    const isLarge = isPage || (isArrow && e.shiftKey)
    if (this.isMarkStep()) {
      e.preventDefault()
      this.moveMarkStep(input, key, isLarge)
      return
    }
    if (!isLarge) return
    e.preventDefault()
    const step = Number(this.getAttr('step', '1')) || 1
    const largeRaw = this.getAttr('large-step', '')
    const large = largeRaw !== '' && Number.isFinite(Number(largeRaw)) ? Number(largeRaw) : step * 10
    const sign = this.keySign(key)
    this.applyKeyboardValue(input, (Number(input.value) || 0) + sign * large)
  }

  /** 键 → 值方向：箭头键受视觉镜像反转（值增长方向与箭头视觉方向一致），页键保持值语义。
   *  视觉镜像 = horizontal 有效反转（reverse 属性 XOR RTL 书写方向）；vertical 用 reverse 属性本身 */
  private keySign(key: string): number {
    let sign = 0
    if (key === 'ArrowRight' || key === 'ArrowUp' || key === 'PageUp') sign = 1
    else if (key === 'ArrowLeft' || key === 'ArrowDown' || key === 'PageDown') sign = -1
    if (sign !== 0 && key.startsWith('Arrow')) {
      const flipped = this.hasAttr('vertical') ? this.hasAttr('reverse') : this.horizontalReverse()
      if (flipped) sign = -sign
    }
    return sign
  }

  /** 水平轴有效视觉反转：reverse 属性 XOR RTL 书写方向（vertical 不随书写方向镜像，不适用） */
  private horizontalReverse(): boolean {
    return this.hasAttr('reverse') !== isRtl(this)
  }

  /** step="mark" 键盘：在刻度档位间跳转（大步跳 MARK_BIG_STEP 档，Home/End 到首末刻度） */
  private moveMarkStep(input: HTMLInputElement, key: string, big: boolean): void {
    const marks = this.parseMarks()
    if (marks.length === 0) return
    if (key === 'Home') {
      this.applyKeyboardValue(input, marks[0]!.value)
      return
    }
    if (key === 'End') {
      this.applyKeyboardValue(input, marks[marks.length - 1]!.value)
      return
    }
    const cur = this.snapToMark(Number(input.value))
    let idx = marks.findIndex((m) => m.value === cur)
    if (idx < 0) idx = 0
    const delta = big ? MARK_BIG_STEP : 1
    const next = clampNum(idx + this.keySign(key) * delta, 0, marks.length - 1)
    this.applyKeyboardValue(input, marks[next]!.value)
  }

  /** 键盘改值：夹取 → 钳制 → 同步全链路 → 派发 input + change（每次按键即一次完整确认） */
  private applyKeyboardValue(input: HTMLInputElement, next: number): void {
    const min = Number(this.getAttr('min', '0'))
    const max = Number(this.getAttr('max', '100'))
    const i = this.thumbs.indexOf(input)
    input.value = String(clampNum(next, min, max))
    if (i >= 0) this.enforceOrder(i)
    this.syncValueAttr()
    this.syncOverlay()
    this.syncNumInputs()
    this.syncMarkPassed()
    const detail = { value: this.emitValue() }
    this.emit('input', detail)
    this.emit('change', detail)
  }

  // ---------- 垂直拖动（pointer 接管） ----------

  /**
   * 垂直拖动接管：原生 range 在 writing-mode 竖直下 Chromium 拖拽 hit-test 失效，
   * 且方向（min 在下）与原生渲染（min 在上）相反，无法用原生拖动。
   * 点击轨道定位到指针处，拖动跟随；值经 step 吸附后走统一 input 事件链路（marks 吸附/事件/联动全复用）。
   */
  private beginVerticalDrag(e: PointerEvent, wrap: HTMLElement): void {
    if (this.verticalDragId !== null) return
    e.preventDefault()
    this.verticalDragId = e.pointerId
    this.dragging = true
    const startValue = this.isMulti() ? JSON.stringify(this.currentValues()) : String(this.thumbs[0]?.value ?? '')
    try {
      wrap.setPointerCapture(e.pointerId)
    } catch {
      /* 捕获失败（如指针已释放）不阻断本次定位 */
    }
    this.applyVerticalPointer(e, wrap)

    const move = (ev: Event): void => {
      if ((ev as PointerEvent).pointerId !== this.verticalDragId) return
      this.applyVerticalPointer(ev as PointerEvent, wrap)
    }
    const finish = (ev: Event): void => {
      if ((ev as PointerEvent).pointerId !== this.verticalDragId) return
      this.verticalDragId = null
      this.dragging = false
      wrap.removeEventListener('pointermove', move)
      wrap.removeEventListener('pointerup', finish)
      wrap.removeEventListener('pointercancel', finish)
      try {
        wrap.releasePointerCapture((ev as PointerEvent).pointerId)
      } catch {
        /* 已释放 */
      }
      // 松手提交：与原生 change 同语义（写回受控 value + 派发 oas-change，值未变不派发）
      const endValue = this.isMulti() ? JSON.stringify(this.currentValues()) : String(this.thumbs[0]?.value ?? '')
      this.syncValueAttr()
      this.syncOverlay()
      this.syncNumInputs()
      this.syncMarkPassed()
      if (endValue !== startValue) {
        this.emit('change', { value: this.emitValue() })
      }
    }
    wrap.addEventListener('pointermove', move)
    wrap.addEventListener('pointerup', finish)
    wrap.addEventListener('pointercancel', finish)
  }

  /** 指针位置 → 值（垂直：默认 min 在下、reverse 镜像；多把手取更近的把手推） */
  private applyVerticalPointer(e: PointerEvent, wrap: HTMLElement): void {
    if (!Number.isFinite(e.clientY)) return
    const min = Number(this.getAttr('min', '0'))
    const max = Number(this.getAttr('max', '100'))
    const rect = wrap.getBoundingClientRect()
    if (!rect.height) return
    let p = (rect.bottom - e.clientY) / rect.height
    if (this.hasAttr('reverse')) p = 1 - p
    p = clampNum(p, 0, 1)
    const span = max - min || 1
    let raw = min + p * span
    // step 吸附（mark/any 不取整，交给 maybeSnapInput 的刻度吸附）
    const stepRaw = this.getAttr('step', '1')
    const step = Number(stepRaw)
    if (stepRaw !== 'mark' && Number.isFinite(step) && step > 0) {
      raw = min + Math.round((raw - min) / step) * step
    }
    raw = clampNum(raw, min, max)
    const target = this.verticalDragTarget(raw)
    if (!target) return
    if (target.value !== String(raw)) {
      target.value = String(raw)
      target.dispatchEvent(new Event('input', { bubbles: true }))
    }
  }

  /** 多把手垂直拖动目标：值更接近哪个把手推哪个 */
  private verticalDragTarget(raw: number): HTMLInputElement | null {
    const n = this.activeThumbCount()
    if (n === 0) return null
    return this.thumbs[this.nearestThumbIndex(raw)] ?? null
  }

  // ---------- range / 多把手模式 ----------

  /** 读取当前各把手实时值，保证升序（range 双值交换保持 lo ≤ hi） */
  private currentRange(): [number, number] {
    const vals = this.currentValues()
    const lo = vals[0] ?? Number(this.getAttr('min', '0'))
    const hi = vals[1] ?? Number(this.getAttr('max', '100'))
    return lo > hi ? [hi, lo] : [lo, hi]
  }

  /** 拖动中钳制 lo ≤ hi（避免跨过另一个 thumb） */
  private clampRangeInputs(): void {
    const minInput = this.thumbs[0]
    const maxInput = this.thumbs[1]
    if (!minInput || !maxInput) return
    const min = Number(this.getAttr('min', '0'))
    const max = Number(this.getAttr('max', '100'))
    let lo = clampNum(Number(minInput.value), min, max)
    let hi = clampNum(Number(maxInput.value), min, max)
    if (lo > hi) [lo, hi] = [hi, lo]
    minInput.value = String(lo)
    maxInput.value = String(hi)
  }

  // ---------- show-input 联动 ----------

  // ---- 读数区 scrub（像素锚定计步：按下记锚点，横移每 4px 计一步，拖回锚点恢复基准值） ----

  /** scrub 开关：默认开；scrub="false" 显式关闭（例如宿主要保留读数框原生文本选择手势） */
  private scrubOff(): boolean {
    return this.getAttr('scrub', 'true') === 'false'
  }

  /** precision 属性（小数位，≥0 整数，上限 100）；未设置/非法返回 null（不修约） */
  private precisionAttr(): number | null {
    const raw = this.getAttr('precision', '').trim()
    if (raw === '') return null
    const n = Number(raw)
    return Number.isFinite(n) && n >= 0 ? Math.min(100, Math.floor(n)) : null
  }

  /** 当前生效步长（mark/any 交给刻度吸附；非法回落 1） */
  private scrubStepValue(): number {
    const raw = this.getAttr('step', '1')
    if (raw === 'mark') return 1
    const n = Number(raw)
    return Number.isFinite(n) && n > 0 ? n : 1
  }

  /** 触屏不启用 scrub：点按即聚焦弹键盘（文本键入优先），且防 scrub 与系统手势打架 */
  private isCoarsePointer(): boolean {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return false
    return window.matchMedia('(pointer: coarse)').matches
  }

  /** 读数框按下：记录锚点（像素 + 基准值），挂 window 级 move/up；不 preventDefault（保留聚焦） */
  private beginNumScrub(i: number, e: PointerEvent): void {
    if (this.numScrub) return
    if (this.scrubOff() || this.injectDisabled() || this.hasAttr('readonly')) return
    if (this.isCoarsePointer()) return
    if (e.pointerType === 'mouse' && e.button !== 0) return
    const base = Number(this.thumbs[i]?.value ?? 0)
    if (!Number.isFinite(base)) return
    this.numScrub = { id: e.pointerId, anchor: beginScrub(e.clientX, base), index: i, active: false, startValue: base }
    window.addEventListener('pointermove', this.onNumScrubMove)
    window.addEventListener('pointerup', this.onNumScrubUp)
    window.addEventListener('pointercancel', this.onNumScrubUp)
  }

  private onNumScrubMove = (e: PointerEvent): void => {
    const s = this.numScrub
    if (!s || e.pointerId !== s.id) return
    const input = this.numInputs[s.index]
    if (!input) return
    if (!s.active) {
      // 激活阈值 = 一个计步单位：不足视为普通点击（保聚焦/文本选择），达到即接管
      if (Math.abs(e.clientX - s.anchor.anchorX) < SCRUB_PX_PER_STEP) return
      s.active = true
      input.setAttribute('data-scrubbing', '')
      // 收起拖动前可能已启动的文本选区（按住拖动不再带原生文本选择跑）
      document.getSelection()?.removeAllRanges()
      try {
        input.setSelectionRange(input.value.length, input.value.length)
        input.setPointerCapture(e.pointerId)
      } catch {
        /* 选区/捕获不可用不阻断 scrub */
      }
    }
    e.preventDefault()
    const min = Number(this.getAttr('min', '0'))
    const max = Number(this.getAttr('max', '100'))
    const v = scrubValueAt(s.anchor, e.clientX, this.scrubStepValue(), e.shiftKey, e.altKey, {
      min,
      max,
      precision: this.precisionAttr() ?? undefined,
    })
    // step="mark" 模式：scrub 计步值吸附最近刻度后再写值——与原生拖动（onThumbInput →
    // maybeSnapInput）同构，保证 thumb / value 属性 / 读数框三路同源（属性序列化不得
    // 拿到档位外的脏值，否则松手后 update 吸附会让宿主状态与组件值漂移）
    const target = this.isMarkStep() ? this.snapToMark(v) : v
    this.applyNumber(s.index, target)
    this.syncValueAttr()
    this.syncOverlay()
    this.syncMarkPassed()
    this.emit('input', { value: this.emitValue() })
  }

  private onNumScrubUp = (e: PointerEvent): void => {
    const s = this.numScrub
    if (!s || e.pointerId !== s.id) return
    this.numScrub = null
    this.finishNumScrubListeners()
    const input = this.numInputs[s.index]
    input?.removeAttribute('data-scrubbing')
    try {
      input?.releasePointerCapture(e.pointerId)
    } catch {
      /* 已释放 */
    }
    if (!s.active) return // 普通点击：交给浏览器聚焦/选中文本
    // 松手提交（对齐原生 change 语义：值变化才派发）；防抖中的延迟提交一并取消
    this.cancelDebounce()
    const end = Number(this.thumbs[s.index]?.value ?? 0)
    if (end !== s.startValue) this.emit('change', { value: this.emitValue() })
  }

  /** 摘除 window 级 scrub 监听（松手/断连共用；幂等） */
  private finishNumScrubListeners(): void {
    window.removeEventListener('pointermove', this.onNumScrubMove)
    window.removeEventListener('pointerup', this.onNumScrubUp)
    window.removeEventListener('pointercancel', this.onNumScrubUp)
  }

  // ---- 双击复位 ----

  /**
   * 双击轨道复位：目标 = reset-value 属性 > min；夹取到 [min, max]，mark 模式吸附最近刻度。
   * 仅单值模式响应（range/多把手无单一复位语义）；走键盘同款链路（同步全量 + input/change 双发）。
   */
  private resetToValue(): void {
    if (this.isMulti() || this.injectDisabled() || this.hasAttr('readonly')) return
    const input = this.thumbs[0]
    if (!input) return
    const min = Number(this.getAttr('min', '0'))
    const max = Number(this.getAttr('max', '100'))
    const raw = Number(this.getAttr('reset-value', ''))
    let target = Number.isFinite(raw) && this.getAttr('reset-value', '').trim() !== '' ? raw : min
    target = clampNum(target, min, max)
    if (this.isMarkStep()) target = this.snapToMark(target)
    this.applyKeyboardValue(input, target)
  }

  private scheduleCommit(i: number): void {
    this.cancelDebounce()
    this.debounceTimer = setTimeout(() => {
      this.debounceTimer = null
      this.commitFromNumber(i, false)
    }, DEBOUNCE_MS)
  }

  private cancelDebounce(): void {
    if (this.debounceTimer !== null) {
      clearTimeout(this.debounceTimer)
      this.debounceTimer = null
    }
  }

  /** 数值输入提交：夹取范围 → 驱动把手 → 输入框归一化 → 派发事件 */
  private commitFromNumber(i: number, emitChange: boolean): void {
    const input = this.numInputs[i]
    if (!input) return
    const raw = input.value.trim()
    const v = Number(raw)
    if (raw === '' || !Number.isFinite(v)) {
      // 非法/空值：还原为当前把手值（非破坏）
      const cur = this.isMulti() ? this.currentValues()[i] : Number(this.thumbs[0]?.value ?? 0)
      input.value = String(cur ?? 0)
      return
    }
    this.applyNumber(i, v)
    this.syncValueAttr()
    this.syncOverlay()
    this.syncMarkPassed()
    if (emitChange) this.emit('change', { value: this.emitValue() })
    else this.emit('input', { value: this.emitValue() })
  }

  /** 把数值写到对应把手（多把手按「推着走」约束：越界推相邻把手保持升序） */
  private applyNumber(i: number, v: number): void {
    const min = Number(this.getAttr('min', '0'))
    const max = Number(this.getAttr('max', '100'))
    const n = this.activeThumbCount()
    if (n <= 1) {
      if (this.thumbs[0]) this.thumbs[0].value = String(clampNum(v, min, max))
      this.syncNumInputs()
      return
    }
    const vals = this.currentValues()
    vals[i] = clampNum(v, min, max)
    for (let j = i + 1; j < vals.length; j++) {
      if (vals[j]! < vals[j - 1]!) vals[j] = vals[j - 1]!
    }
    for (let j = i - 1; j >= 0; j--) {
      if (vals[j]! > vals[j + 1]!) vals[j] = vals[j + 1]!
    }
    for (let j = 0; j < n; j++) {
      if (this.thumbs[j]) this.thumbs[j]!.value = String(vals[j])
    }
    this.syncNumInputs()
  }

  /**
   * 受控状态写回宿主 value 属性（与 switch/radio-group 一致的双向受控语义）：
   * 单值写数字字符串，多把手写 JSON 数组字符串（表单序列化/宿主 JSON.parse 友好）。
   * 宿主 getAttribute / 表单收集 / 外部读状态可直接取最新值，不必缓存 oas-change detail。
   * 写回触发的 attributeChangedCallback → update() 为幂等同步（值相同无循环、不再 emit）。
   */
  private syncValueAttr(): void {
    if (this.isMulti()) {
      this.setAttribute('value', JSON.stringify(this.currentValues()))
    } else if (this.thumbs[0]) {
      this.setAttribute('value', String(this.thumbs[0].value))
    }
  }

  /** 把手 → 输入框单向同步（拖动/提交后） */
  private syncNumInputs(): void {
    if (!this.hasAttr('show-input')) return
    if (this.isMulti()) {
      const vals = this.currentValues()
      for (let i = 0; i < vals.length; i++) {
        if (this.numInputs[i]) this.numInputs[i]!.value = String(vals[i])
      }
    } else if (this.numInputs[0] && this.thumbs[0]) {
      this.numInputs[0].value = String(Number(this.thumbs[0].value))
    }
  }

  // ---------- 自定义滑块 / 填充 / 气泡 ----------

  private hasCustomThumb(): boolean {
    return !!this.querySelector('template[slot="custom-thumb"]') || !!this.querySelector('[slot="custom-thumb"]')
  }

  /** 把 light DOM 自定义滑块内容（template 或元素）克隆进每个可见滑块 */
  private syncThumbContent(): void {
    const templateEl = this.querySelector<HTMLTemplateElement>('template[slot="custom-thumb"]')
    const liveEl = !templateEl ? this.querySelector<HTMLElement>('[slot="custom-thumb"]') : null
    const contents = [...this.shadow.querySelectorAll<HTMLElement>('.thumb-content')]
    if (!templateEl && !liveEl) {
      for (const c of contents) {
        c.dataset.srcKey = ''
        c.textContent = ''
      }
      return
    }
    const key = templateEl ? `t:${templateEl.innerHTML}` : `l:${liveEl!.outerHTML}`
    for (const content of contents) {
      if (content.dataset.srcKey === key) continue
      content.dataset.srcKey = key
      content.textContent = ''
      if (templateEl) {
        const frag = templateEl.content.cloneNode(true) as DocumentFragment
        content.append(...Array.from(frag.childNodes))
      } else {
        content.append(liveEl!.cloneNode(true))
      }
    }
  }

  /** 统一同步填充区、自定义视觉层、值气泡的位置与显隐 */
  private syncOverlay(): void {
    const fill = this.shadow.querySelector<HTMLElement>('.fill')
    if (!fill) return
    const min = Number(this.getAttr('min', '0'))
    const max = Number(this.getAttr('max', '100'))
    const span = max - min || 1
    // 视觉反转取轴相关值：horizontal = 有效反转（reverse XOR RTL）；vertical = reverse 属性本身
    const reverse = this.hasAttr('vertical') ? this.hasAttr('reverse') : this.horizontalReverse()
    const vertical = this.hasAttr('vertical')
    const multi = this.isMulti()
    const focused = this.hasAttribute('data-focused')
    // 气泡可见：常显 > 拖动 > 聚焦（focus 语义，不依赖 show-tooltip）
    const tipsVisible = this.hasAttr('show-tooltip') || this.hasAttr('tooltip-always') || this.dragging || focused

    // 立体声电平表：仅单值 + 圆推子（默认形态）渲染；有值时恒启用自定义视觉层（电平条需常显）。
    // multi/range 无对应单值语义 → 不解析也不触发自定义视觉层（避免无意义地改变拇指形态）
    const levels = this.isPointerThumb() || multi ? null : this.parseLevels()
    const hasLevels = levels !== null

    // 拖动/聚焦/常显中启用自定义视觉层（拖动时临时显示值气泡，无需 show-tooltip）；
    // 垂直模式恒启用：原生 thumb 隐藏，.custom-thumb 承担默认拇指视觉与拖动反馈；
    // pointer 形态恒启用：细指针造型由 .custom-thumb 承担（原生拇指无法跨浏览器统一为细条）；
    // 电平表恒启用：立体声电平需常显，不能只在拖动/聚焦时出现
    const useOverlay =
      vertical ||
      this.isPointerThumb() ||
      hasLevels ||
      this.hasCustomThumb() ||
      this.hasAttr('show-tooltip') ||
      this.hasAttr('tooltip-always') ||
      this.dragging ||
      focused

    const vals = multi ? this.currentValues() : null
    // 填充区间：单值 [start-point, value]（起点缺省 min），多把手 [首把手, 末把手]
    const sp = this.startPointValue(min, max)
    const lo = vals ? vals[0]! : sp
    const hi = vals ? vals[vals.length - 1]! : Number(this.thumbs[0]?.value ?? 0)
    const pctOf = (v: number): number => ((v - min) / span) * 100
    // 视觉轴归一（水平从左起算 / 垂直从上起算）：水平默认 min 在左、垂直默认 min 在下，
    // reverse 各自镜像（horizontal 的 reverse 已并入 RTL 书写方向的有效反转）
    const vis = (pct: number): number => (vertical ? (reverse ? pct : 100 - pct) : reverse ? 100 - pct : pct)
    fill.dataset.pct = String(reverse ? 100 - pctOf(hi) : pctOf(hi))
    // 定位轴切换（vertical 用 top/height），切轴时清理另一轴的位置与尺寸残留
    const axis = vertical ? 'top' : 'left'
    const sizeProp = vertical ? 'height' : 'width'
    const offAxis = vertical ? 'left' : 'top'
    const offSize = vertical ? 'width' : 'height'
    fill.style.removeProperty(offAxis)
    fill.style.removeProperty(offSize)
    // 填充边与 thumb 中心精确对齐：Chromium 原生 thumb 行程为 [0, 长-直径]
    // （中心 = pct×(长-直径)+半径，实测测定，见 qa-regression 拇指对齐断言），
    // 填充继续用 % 会在两端偏差最多半个直径（7px）。有布局尺寸时按像素对齐
    // （thumb 同公式）；无尺寸（SSR/hidden/测试环境）回落 % 保持旧行为。
    const trackInput = this.thumbs[Math.max(0, this.thumbs.length - 1)] ?? this.thumbs[0]
    const trackLen = vertical ? (trackInput?.clientHeight ?? 0) : (trackInput?.clientWidth ?? 0)
    if (trackLen) {
      const size = this.thumbSize()
      const posPx = (v: number): number => {
        const norm = clampNum((v - min) / span, 0, 1)
        const visual = vertical ? (reverse ? norm : 1 - norm) : reverse ? 1 - norm : norm
        return visual * Math.max(trackLen - size, 0) + size / 2
      }
      // 单值且无显式 start-point：填充从轨道视觉起点边（水平左/垂直底）开始贴边——
      // 用 posPx(min) 会在起点留出拇指半径（size/2）的灰缝（用户实测）；多把手/显式起点仍从 lo thumb 中心起
      const hasStartPoint = this.hasAttr('start-point')
      const edgeStart = vertical ? (reverse ? 0 : trackLen) : reverse ? trackLen : 0
      const a = multi || hasStartPoint ? posPx(lo) : edgeStart
      const b = posPx(hi)
      fill.style.setProperty(axis, `${Math.min(a, b)}px`)
      fill.style.setProperty(sizeProp, `${Math.abs(b - a)}px`)
    } else {
      const hasStartPoint = this.hasAttr('start-point')
      const edgeStart = vertical ? (reverse ? 0 : 100) : reverse ? 100 : 0
      const a = multi || hasStartPoint ? vis(pctOf(lo)) : edgeStart
      const b = vis(pctOf(hi))
      fill.style.setProperty(axis, `${Math.min(a, b)}%`)
      fill.style.setProperty(sizeProp, `${Math.abs(b - a)}%`)
    }

    // 逐把手视觉层：位置/气泡/显隐
    for (let i = 0; i < this.overlays.length; i++) {
      const th = this.overlays[i]!
      const active = i < this.thumbs.length && i < this.thumbCountCache
      th.hidden = true
      if (!active) continue
      const n = this.thumbCountCache
      th.dataset.thumb = n === 1 ? 'value' : n === 2 ? (i === 0 ? 'min' : 'max') : String(i)
      const v = vals ? (vals[i] ?? min) : Number(this.thumbs[0]?.value ?? 0)
      const tip = th.querySelector<HTMLElement>('.thumb-tip')
      if (tip) {
        tip.textContent = this.formatValue(v)
        tip.hidden = !tipsVisible
      }
      const levelsEl = th.querySelector<HTMLElement>('.levels')
      if (levelsEl) {
        // levels 是单个 {left,right} 全局值：仅单值模式渲染（多拇指/range 无对应语义，避免每把手一副重复电平）
        if (levels && useOverlay && !this.isMulti()) {
          levelsEl.hidden = false
          for (const ch of ['left', 'right'] as const) {
            const barFill = levelsEl.querySelector<HTMLElement>(`.level-bar[data-channel='${ch}'] .level-fill`)
            if (barFill) barFill.style.height = `${(ch === 'left' ? levels.left : levels.right) * 100}%`
          }
        } else {
          levelsEl.hidden = true
        }
      }
      if (!useOverlay) continue
      th.hidden = false
      const norm = vis(pctOf(v))
      th.dataset.pct = String(norm)
      const pos = this.thumbPos(norm)
      if (vertical) {
        th.style.top = pos
        th.style.left = ''
      } else {
        th.style.left = pos
        th.style.top = ''
      }
    }

    this.syncAriaValueText()

    if (useOverlay) this.setAttribute('data-custom-thumb', '')
    else this.removeAttribute('data-custom-thumb')
    this.toggleAttribute('data-thumb-content', this.hasCustomThumb())
  }

  /** aria-valuetext 同步：有格式化通道时写格式化文本（读屏与气泡同源），否则移除回落 valuenow */
  private syncAriaValueText(): void {
    const apply = (r: HTMLInputElement | undefined, v: number): void => {
      if (!r) return
      if (this.hasFormatter()) r.setAttribute('aria-valuetext', this.formatValue(v))
      else r.removeAttribute('aria-valuetext')
    }
    if (this.isMulti()) {
      const vals = this.currentValues()
      for (let i = 0; i < vals.length; i++) apply(this.thumbs[i], vals[i]!)
    } else {
      apply(this.thumbs[0], Number(this.thumbs[0]?.value ?? 0))
    }
  }

  /** 自定义滑块像素定位：与原生 thumb「中心」对齐——原生 thumb 左缘 = pct×(总长-直径)，
      中心再 + 半径；custom-thumb 以 translate(-50%,-50%) 按中心定位，少了半径会偏 7px。
      vertical 时沿 Y 轴换算 */
  private thumbPos(normPct: number): string {
    const input = this.thumbs[0]
    const vertical = this.hasAttr('vertical')
    const track = vertical ? (input?.clientHeight ?? 0) : (input?.clientWidth ?? 0)
    if (!track) return `${normPct}%`
    const size = this.thumbSize()
    const pos = (normPct / 100) * (track - size) + size / 2
    return `${pos}px`
  }

  // ---------- marks 刻度 / show-stops 刻度点 ----------

  /** 解析 marks 属性：JSON 对象 { value: label } 或 JSON 数组 [value] / [{ value, label }] */
  private parseMarks(): MarkEntry[] {
    const raw = this.getAttr('marks', '')
    if (!raw) return []
    try {
      const parsed = JSON.parse(raw)
      if (Array.isArray(parsed)) {
        return parsed
          .map((m): MarkEntry | null => {
            if (typeof m === 'number' && Number.isFinite(m)) return { value: m, label: String(m) }
            if (m && typeof m === 'object' && Number.isFinite(m.value)) {
              return { value: m.value, label: m.label != null ? String(m.label) : String(m.value) }
            }
            return null
          })
          .filter((m): m is MarkEntry => m !== null)
          .sort((a, b) => a.value - b.value)
      }
      if (parsed && typeof parsed === 'object') {
        return Object.entries(parsed)
          .map(([k, v]) => ({ value: Number(k), label: String(v) }))
          .filter((m) => Number.isFinite(m.value))
          .sort((a, b) => a.value - b.value)
      }
    } catch {
      /* 非法 JSON 视为无刻度 */
    }
    return []
  }

  /** 刻度条目：marks 优先（自带标签）；无 marks 且 show-stops 时按 step 生成刻度点（无标签） */
  private computeTicks(): MarkEntry[] {
    const marks = this.parseMarks()
    if (marks.length > 0) return marks
    if (!this.hasAttr('show-stops')) return []
    const rawStep = this.getAttr('step', '1')
    const step = Number(rawStep)
    if (rawStep === 'mark' || !Number.isFinite(step) || step <= 0) return []
    const min = Number(this.getAttr('min', '0'))
    const max = Number(this.getAttr('max', '100'))
    const count = Math.floor((max - min) / step + 1e-6) + 1
    if (count > MAX_STOPS || count < 2) return []
    return Array.from({ length: count }, (_, i) => ({
      value: Number((min + i * step).toFixed(4)),
      label: '',
    }))
  }

  /** 增量同步刻度区：签名变化才重建节点，否则只更新经过状态 */
  private syncMarks(): void {
    const marksEl = this.shadow.querySelector<HTMLElement>('.marks')
    if (!marksEl || this.thumbs.length === 0) return
    const ticks = this.computeTicks()
    if (ticks.length === 0) {
      marksEl.hidden = true
      this.marksKey = ''
      return
    }
    marksEl.hidden = false
    const min = Number(this.getAttr('min', '0'))
    const max = Number(this.getAttr('max', '100'))
    const reverse = this.hasAttr('vertical') ? this.hasAttr('reverse') : this.horizontalReverse()
    const vertical = this.hasAttr('vertical')
    const key =
      `${vertical ? 'v' : ''}${reverse ? 'r' : ''}:${min}:${max}:${this.getAttr('step', '1')}:${this.hasAttr('show-stops')}|` +
      ticks.map((m) => `${m.value}:${m.label}`).join('|')
    if (key !== this.marksKey) {
      this.marksKey = key
      this.renderMarks(marksEl, ticks, min, max)
    }
    const [lo, hi] = this.coveredRange()
    this.updateMarkPassed(marksEl, lo, hi)
  }

  /** 当前选区覆盖的值区间（刻度经过判定）：多把手 [首, 末]；单值 [start-point, value] */
  private coveredRange(): [number, number] {
    if (this.isMulti()) {
      const vals = this.currentValues()
      return [vals[0] ?? 0, vals[vals.length - 1] ?? 0]
    }
    const min = Number(this.getAttr('min', '0'))
    const max = Number(this.getAttr('max', '100'))
    const v = Number(this.thumbs[0]?.value ?? 0)
    const sp = this.startPointValue(min, max)
    return [Math.min(sp, v), Math.max(sp, v)]
  }

  private renderMarks(container: HTMLElement, ticks: MarkEntry[], min: number, max: number): void {
    const span = max - min || 1
    const reverse = this.hasAttr('vertical') ? this.hasAttr('reverse') : this.horizontalReverse()
    const vertical = this.hasAttr('vertical')
    container.innerHTML = ''
    for (const mark of ticks) {
      const item = document.createElement('div')
      item.className = 'mark'
      item.setAttribute('part', 'mark')
      item.setAttribute('data-value', String(mark.value))
      item.setAttribute('data-passed', 'false')
      const pct = ((mark.value - min) / span) * 100
      // 视觉轴归一：水平 min 在左、垂直 min 在下（reverse 镜像），与 fill/thumb 同约定
      const norm = vertical ? (reverse ? pct : 100 - pct) : reverse ? 100 - pct : pct
      // 定位轴切换（vertical 沿 Y 轴），切轴时清理另一轴残留
      if (vertical) {
        item.style.top = `${norm}%`
        item.style.left = ''
      } else {
        item.style.left = `${norm}%`
        item.style.top = ''
      }
      const dot = document.createElement('div')
      dot.className = 'mark-dot'
      item.append(dot)
      // show-stops 生成的刻度点无标签（不渲染标签节点，避免占位高度）
      if (mark.label !== '') {
        const label = document.createElement('div')
        label.className = 'mark-label'
        label.textContent = mark.label
        item.append(label)
      }
      container.appendChild(item)
    }
  }

  /** 更新各刻度「是否被当前选区覆盖」状态（data-passed），只改属性不重建节点 */
  private updateMarkPassed(container: HTMLElement, low: number, high: number): void {
    for (const item of container.querySelectorAll<HTMLElement>('.mark')) {
      const v = Number(item.getAttribute('data-value'))
      const passed = v >= low && v <= high
      const flag = passed ? 'true' : 'false'
      if (item.getAttribute('data-passed') !== flag) {
        item.setAttribute('data-passed', flag)
      }
    }
  }

  /** 拖动实时变化时，仅刷新经过状态（不动 value 属性，保持受控语义） */
  private syncMarkPassed(): void {
    const marksEl = this.shadow.querySelector<HTMLElement>('.marks')
    if (!marksEl || marksEl.hidden) return
    const [lo, hi] = this.coveredRange()
    this.updateMarkPassed(marksEl, lo, hi)
  }
}
